import { defineStore } from 'pinia';
import OffersService, { OfferStatus } from '@/api/offersService';
import { useAuthStore } from '@/store/AuthStore';

export type OfferRecord = {
  id: number;
  status?: OfferStatus | string;
  [key: string]: unknown;
};

export type OffersLastAPICall = {
  total?: number;
  offset?: number;
  limit?: number;
};

type OffersQueryParams = {
  keyword?: string;
  status?: OfferStatus;
  limit?: number;
  offset?: number;
  reset?: boolean;
  [key: string]: unknown;
};

type UpdateOfferParams = {
  id: number;
  status: OfferStatus;
  [key: string]: unknown;
};

export const useOffersStore = defineStore({
  id: 'offers',
  state: () => {
    return {
      _offers: { '': [] as OfferRecord[] } as Record<string, OfferRecord[]>,
      keyword: '',
      _lastAPICall: {} as OffersLastAPICall,
    };
  },
  getters: {
    offers: (state): OfferRecord[] => {
      return state._offers[state.keyword] || [];
    },
    lastAPICall: (state): OffersLastAPICall => {
      return state._lastAPICall || {};
    },
  },
  actions: {
    getBaseSegment(): string {
      const auth = useAuthStore();
      const role = auth.userRole;

      const endpoints: Record<string, string> = {
        gallery: 'galleries',
        private_seller: 'private_sellers',
        artist: 'artists',
        auctioneer: 'auctioneers',
      };

      return endpoints[role] || 'galleries';
    },

    async getOffers(params: OffersQueryParams) {
      return await OffersService.getOffers(this.getBaseSegment(), params);
    },

    async updateOffer(params: UpdateOfferParams) {
      return await OffersService.updateOffer(this.getBaseSegment(), params);
    },

    async items({
      keyword = '',
      limit = 20,
      offset = 0,
      reset = false,
      ...extra
    }: OffersQueryParams = {}) {
      if (reset) {
        this._offers = { '': [] };
      }

      const data = await this.getOffers({ keyword, limit, offset, ...extra });
      this.keyword = keyword;

      if (data?.success) {
        const items: OfferRecord[] = data?.data?.items ?? [];

        this._offers = {
          ...this._offers,
          [keyword]: items,
        };

        this._lastAPICall = {
          total: Number(data?.data?.total ?? 0),
          offset: Number(data?.data?.offset ?? offset),
          limit: Number(data?.data?.limit ?? limit),
        };
      }

      return data;
    },

    clearState() {
      this._offers = { '': [] };
      this.keyword = '';
      this._lastAPICall = {};
    },
  },
});
